import { makeObservable, action, observable } from 'mobx';
import { BaseService } from '../base';
import { FAST_ESCROW_ORDER_TYPE } from '@/stores/order/order.interface';
import { UpcomingResponseModel, UpcomingListModel } from './model';
import Upcoming from './interface';

/** 单个 Tab 的独立状态。两个 Tab 各自一套，互不共享(对齐 Outstanding CacheList 模式) */
export interface IUpcomingTabState {
  loaded: boolean;
  /** 拉取失败(code!==0)。用来区分「加载成功但没有订单」和「加载失败」 */
  loadFailed: boolean;
  hasMore: boolean;
  page_index: number;
  dataList: UpcomingListModel[];
  /** header 的金额/订单额/放款比例/放款时间都取自这里，每个 Tab 各自一份 */
  upcomingState: UpcomingResponseModel;
}

const createTabState = (): IUpcomingTabState => ({
  loaded: true,
  loadFailed: false,
  hasMore: true,
  page_index: 0,
  dataList: [],
  upcomingState: new UpcomingResponseModel(),
});

export class UpcomingService extends BaseService {
  // Fulfilled to Buyer(order_type=0：标准/Loan#2；非 cache 用户也走这套)
  @observable fulfilledState: IUpcomingTabState = createTabState();
  // Advanced Shopee Fulfilled(order_type=1：Cache Loan#1)
  @observable advancedState: IUpcomingTabState = createTabState();

  private page_size = 10;

  constructor() {
    super();
    makeObservable(this);
  }

  getTabState(orderType: number): IUpcomingTabState {
    return orderType === FAST_ESCROW_ORDER_TYPE.CACHE ? this.advancedState : this.fulfilledState;
  }

  @action
  private setTabState(orderType: number, patch: Partial<IUpcomingTabState>) {
    if (orderType === FAST_ESCROW_ORDER_TYPE.CACHE) {
      this.advancedState = { ...this.advancedState, ...patch };
    } else {
      this.fulfilledState = { ...this.fulfilledState, ...patch };
    }
  }

  /** 离开页面时整体重置两套状态；Tab 切换不重置(切回复用缓存) */
  @action
  initInfo() {
    this.fulfilledState = createTabState();
    this.advancedState = createTabState();
  }

  @action
  async fetchInfo(orderType = 0) {
    const state = this.getTabState(orderType);
    // 该 Tab 没有更多数据、或它自己的请求还在飞，不再发起
    if (!state.hasMore || !state.loaded) return;
    this.setTabState(orderType, { loaded: false, loadFailed: false });

    const { code, result } = await this.post(Upcoming.RefactorAPI.get_upcoming, {
      body: {
        // Cache：0=Fulfilled to Buyer(标准/Loan#2)，1=Advanced Shopee Fulfilled(Loan#1)。非 cache 传 0
        order_type: orderType,
        pagination: {
          page_index: state.page_index + 1,
          page_size: this.page_size,
          cursor:
            state.dataList.length > 0 ? state.dataList[state.dataList.length - 1]?.id ?? '0' : '0',
        },
      },
    });
    // 写回按发请求时的 orderType 落到自己那套状态，与当前激活的 Tab 无关——
    // 慢请求晚到也只会写进自己 Tab 的数据，不会窜到另一个 Tab
    if (code === 0) {
      const { page_info, list = [] } = result;
      const cur = this.getTabState(orderType);
      this.setTabState(orderType, {
        dataList: [...cur.dataList, ...(list ?? [])],
        hasMore: page_info.has_more,
        page_index: page_info.page_index,
        upcomingState: { ...cur.upcomingState, ...result },
        loaded: true,
      });
      return result;
    }
    // 页面据此展示失败态；否则 upcomingState 是空的，会渲染出裸 "Rp"/"%" 并错显「没有订单」
    this.setTabState(orderType, { loaded: true, hasMore: false, loadFailed: true });
  }
}
